import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import { HttpClient, HttpContext, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { SILENT_ERRORS } from '../interceptors/error.interceptor';
import {
  ApiCollectionResponse,
  ApiSingleResponse,
} from '../interfaces/responses/api-response';
import { Test } from '../interfaces/test';
import {
  PracticeGenerationRequest,
  PracticeGenerationResult,
  PracticePool,
} from '../interfaces/practice';

// ── DTOs ────────────────────────────────────────────────────────────────────

export interface CreateTestSessionDto {
  testId: number;
}

/**
 * Body of `POST /api/student-tests/practice/generate` — backend builds an
 * ephemeral practice test for the authenticated student from these criteria.
 * Constraints: subjectId must be in the student's learningPath, themeIds must
 * belong to subjectId, and an academic period must be active server-side.
 */
export interface GeneratePracticeTestDto {
  subjectId: number;
  themeIds?: number[];
  questionCount: number;
  timeLimitMinutes?: number | null;
}

export interface GeneratedPracticeTestDto {
  testId: number;
  questionsGenerated: number;
  academicPeriodId?: number;
  test?: Test;
}

export interface StudentTestSessionDto {
  id: number;
  testId: number;
  studentId: string;
  startedAt: string;
  deadline: string | null;
  attemptNumber: number;
  status: string;
  ipAddress: string;
  userAgent: string;
  /**
   * Réponses déjà enregistrées. Une session reprise revient du démarrage avec
   * sa copie : c'est ce qui permet de la retrouver sur un autre appareil.
   */
  studentAnswers?: { questionId: number; answerId: number | null }[];
  /**
   * Secondes restantes selon le serveur, `null` sans limite. Compté par lui :
   * l'horloge du poste n'y entre pas.
   */
  secondsRemaining?: number | null;
  /**
   * Temps passé dans la passation, tel que l'écran l'a transmis au dernier
   * enregistrement. Sert à le cumuler d'un appareil à l'autre.
   */
  timeSpentSeconds?: number | null;
}

export interface SubmitAnswerItemDto {
  questionId: number;
  answerId: number | null;
  textAnswer?: string;
  numericAnswer?: number;
  timeSpentSeconds?: number;
}

export interface SessionMetadataDto {
  tabSwitchCount: number;
  focusLossCount: number;
  fullscreenExitCount: number;
  pageVisibilityChanges: number;
  additionalInfo?: Record<string, string>;
}

export interface CompleteTestSessionDto {
  answers: SubmitAnswerItemDto[];
  timeSpentSeconds?: number;
  metadata?: SessionMetadataDto;
}

/**
 * Une option de réponse, telle que le serveur la rend après correction.
 *
 * <p>L'identifiant s'appelle `id` : c'est `AnswerOptionDto` côté API. Il a
 * longtemps été lu ici sous le nom `answerId`, qui n'existe pas dans la
 * charge utile — la carte des réponses se construisait alors sur des clés
 * `undefined` et plus rien ne se surlignait. `answerId` reste toléré à la
 * lecture, sans être attendu.</p>
 */
export interface AnswerDetailDto {
  id: number;
  answerId?: number;
  content?: string;
  explanation?: string | null;
  displayOrder?: number | null;
  isCorrect: boolean;
  wasSelected: boolean;
}

export interface QuestionDetailDto {
  questionId: number;
  questionTitle?: string;
  questionType?: string;
  points: number;
  earnedPoints: number;
  isCorrect: boolean;
  selectedAnswerIds?: number[];
  correctAnswerIds?: number[];
  allAnswers?: AnswerDetailDto[];
}

export interface TestScoreResultDto {
  sessionId: number;
  testId?: number;
  testTitle?: string;
  testMode?: string;
  /** Points obtenus. Le serveur les nomme `earnedPoints` ; `score` reste lu par tolérance. */
  earnedPoints?: number;
  score: number;
  totalPoints?: number;
  percentage: number;
  percentageScore?: number;
  passingPercentage?: number;
  /** Barème du test : la note se lit sur ce total. 20 par défaut. */
  gradingScale?: number;
  /**
   * Note ramenée sur le barème — ce que l'élève lit.
   *
   * <p>Dérivée du rapport points obtenus / points possibles : un test de 18
   * questions à 1 point se rend sur 20 sans que ses questions aient été
   * repesées.</p>
   */
  scaledScore?: number;
  /** Seuil de réussite exprimé sur le même barème. */
  scaledPassingScore?: number;
  passed: boolean;
  correctAnswers: number;
  wrongAnswers: number;
  totalQuestions: number;
  unansweredQuestions?: number;
  timeLimitMinutes?: number | null;
  timeSpentSeconds?: number | null;
  completedAt?: string | null;
  questionDetails?: QuestionDetailDto[];
}

export interface SessionFullDto {
  id: number;
  studentId?: string;
  testId?: number;
  testTitle?: string;
  testMode?: string;
  subjectName?: string;
  status?: string;
  startedAt?: string;
  completedAt?: string | null;
  timeSpentSeconds?: number | null;
  attemptNumber?: number;
  score?: number | null;
  percentage?: number | null;
  percentageScore?: number | null;
  passed?: boolean | null;
  correctAnswers?: number | null;
  wrongAnswers?: number | null;
  unansweredQuestions?: number | null;
  totalQuestions?: number | null;
  totalPoints?: number | null;
  earnedPoints?: number | null;
  tabSwitchCount?: number;
  focusLossCount?: number;
  fullscreenExitCount?: number;
  pageVisibilityChanges?: number;
  metadata?: {
    tabSwitchCount?: number; tabSwitches?: number;
    focusLossCount?: number; focusLosses?: number;
    fullscreenExitCount?: number; fullscreenExits?: number;
    pageVisibilityChanges?: number;
  };
  sessionMetadata?: {
    tabSwitchCount?: number; tabSwitches?: number;
    focusLossCount?: number; focusLosses?: number;
    fullscreenExitCount?: number; fullscreenExits?: number;
    pageVisibilityChanges?: number;
  };
  /** Primary source for grading — each entry has isCorrect flag */
  studentAnswers?: Array<{ isCorrect?: boolean; [key: string]: any }>;
  questionDetails?: QuestionDetailDto[];
  [key: string]: any;
}

// ── Service ─────────────────────────────────────────────────────────────────

@Injectable({
  providedIn: 'root',
})
export class StudentTestService {
  private baseUrl = `${environment.BASE_URL}/student-tests`;

  constructor(private http: HttpClient) {}

  getTestsByTestMode(
    studentId: string,
    testMode: string,
  ): Observable<ApiCollectionResponse<Test>> {
    return this.http.get<ApiCollectionResponse<Test>>(
      `${this.baseUrl}/available/mode/${testMode}`,
    );
  }

  /**
   * Start a new test session on the backend.
   * Returns the session DTO with the generated sessionId.
   *
   * <p>`silencieuse` : l'écran de passation explique lui-même un refus (plus
   * de tentative, test retiré). Sans cela, un 403 renvoyait sur /unauthorized
   * — « accès refusé » pour un élève qui a simplement épuisé ses tentatives.</p>
   */
  startTestSession(
    dto: CreateTestSessionDto,
    { silencieuse = false }: { silencieuse?: boolean } = {},
  ): Observable<ApiSingleResponse<StudentTestSessionDto>> {
    return this.http.post<ApiSingleResponse<StudentTestSessionDto>>(
      `${this.baseUrl}/sessions/start`,
      dto,
      { context: new HttpContext().set(SILENT_ERRORS, silencieuse) },
    );
  }

  /**
   * Complete a test session: submits all answers + antiflood metadata,
   * marks the session as finished and returns the detailed score.
   */
  completeTestSession(
    sessionId: number,
    dto: CompleteTestSessionDto,
  ): Observable<ApiSingleResponse<TestScoreResultDto>> {
    return this.http.post<ApiSingleResponse<TestScoreResultDto>>(
      `${this.baseUrl}/sessions/${sessionId}/complete`,
      dto,
    );
  }

  /**
   * Fetch full session detail including questionDetails and cheating metadata.
   * Same endpoint used by the backend when a student submits — returns the
   * authoritative grading data (correctAnswers, cheating stats, questionDetails).
   */
  getSessionById(sessionId: number): Observable<ApiSingleResponse<SessionFullDto>> {
    return this.http.get<ApiSingleResponse<SessionFullDto>>(
      `${this.baseUrl}/sessions/${sessionId}`,
    );
  }

  /**
   * Relit la correction d'une copie rendue : score et détail par question,
   * options et bonne réponse comprises.
   *
   * <p>C'est ce qui rend une copie consultable après coup. Le même contenu
   * n'existait que dans la réponse à la remise, donc uniquement dans l'écran
   * affiché juste après : rouvrir la copie depuis l'historique ne trouvait
   * plus rien.</p>
   */
  getSessionScore(sessionId: number): Observable<ApiSingleResponse<TestScoreResultDto>> {
    return this.http.get<ApiSingleResponse<TestScoreResultDto>>(
      `${this.baseUrl}/sessions/${sessionId}/score`,
    );
  }

  getStudentProgression(): Observable<ApiSingleResponse<any>> {
    return this.http.get<ApiSingleResponse<any>>(`${this.baseUrl}/progression`);
  }

  /**
   * Download the PDF report for a completed session as a Blob.
   * @param lang - 'fr' (default) | 'en' | 'ar'
   */
  downloadTestReport(sessionId: number, lang: string = 'fr'): Observable<Blob> {
    const params = new HttpParams().set('lang', lang);
    return this.http.get(`${this.baseUrl}/sessions/${sessionId}/report`, {
      params,
      responseType: 'blob',
    });
  }

  /** All tests the current student has taken (history). */
  getMyTests(): Observable<ApiCollectionResponse<Test>> {
    return this.http.get<ApiCollectionResponse<Test>>(this.baseUrl);
  }

  /** All available tests the current student can take. */
  getAvailable(): Observable<ApiCollectionResponse<Test>> {
    return this.http.get<ApiCollectionResponse<Test>>(
      `${this.baseUrl}/available`,
    );
  }

  /** Available tests filtered by mode. */
  getAvailableByMode(mode: string): Observable<ApiCollectionResponse<Test>> {
    return this.http.get<ApiCollectionResponse<Test>>(
      `${this.baseUrl}/available/mode/${mode}`,
    );
  }

  /**
   * Tests disponibles pour une matière et un mode donnés.
   *
   * <p>C'est l'appel du module de test côté élève : chaque écran est adossé à
   * une matière, et le filtrage se fait côté serveur plutôt qu'en rapatriant
   * tout le catalogue.</p>
   *
   * <p>`silencieuse` : l'appelant gère l'échec seul, sans fenêtre d'erreur ni
   * redirection (voir {@link SILENT_ERRORS}). À réserver aux usages où cette
   * liste est une information secondaire — l'espace tests, dont c'est la
   * donnée principale, garde le comportement par défaut.</p>
   */
  getAvailableByModeAndSubject(
    mode: string,
    subjectId: number,
    options: { silencieuse?: boolean } = {},
  ): Observable<ApiCollectionResponse<Test>> {
    return this.http.get<ApiCollectionResponse<Test>>(
      `${this.baseUrl}/available/mode/${mode}/subject/${subjectId}`,
      options.silencieuse ? { context: new HttpContext().set(SILENT_ERRORS, true) } : {},
    );
  }

  /**
   * Generate a practice test on the fly for the current student.
   * Backend creates an ephemeral Test from the criteria (subject + optional
   * themes + questionCount) within the active academic period.
   * @see GeneratePracticeTestDto
   */
  generatePracticeTest(
    dto: GeneratePracticeTestDto,
  ): Observable<ApiSingleResponse<GeneratedPracticeTestDto>> {
    return this.http.post<ApiSingleResponse<GeneratedPracticeTestDto>>(
      `${this.baseUrl}/practice/generate`,
      dto,
    );
  }

  /**
   * Vivier d'entraînement disponible, avant génération.
   *
   * <p>À appeler pour alimenter l'indicateur de diversité : il permet
   * d'annoncer à l'élève combien de tests réellement distincts son vivier
   * autorise, plutôt que de le laisser découvrir la répétition.</p>
   */
  getPracticePool(
    subjectId: number,
    themeIds: number[] = [],
  ): Observable<ApiSingleResponse<PracticePool>> {
    let params = new HttpParams().set('subjectId', subjectId.toString());
    for (const id of themeIds) {
      // Répété dans la query string : themeIds=1&themeIds=2.
      params = params.append('themeIds', id.toString());
    }
    return this.http.get<ApiSingleResponse<PracticePool>>(
      `${this.baseUrl}/practice/pool`,
      { params },
    );
  }

  /**
   * Compose un test d'entraînement pondéré par la progression de l'élève.
   *
   * <p>Remplace {@link generatePracticeTest} : ce moteur favorise les chapitres
   * mal maîtrisés, écarte les questions vues récemment et conserve une graine
   * reproductible.</p>
   */
  generatePractice(
    request: PracticeGenerationRequest,
  ): Observable<ApiSingleResponse<PracticeGenerationResult>> {
    return this.http.post<ApiSingleResponse<PracticeGenerationResult>>(
      `${this.baseUrl}/practice/generate`,
      request,
    );
  }

  /** All sessions (attempts) for a given test. */
  getMyAttempts(testId: number): Observable<ApiCollectionResponse<any>> {
    return this.http.get<ApiCollectionResponse<any>>(
      `${this.baseUrl}/${testId}/sessions`,
    );
  }

  /**
   * Toutes les tentatives de l'élève sur les tests d'une matière, la plus
   * récente d'abord.
   *
   * <p>Alimente l'historique de l'écran matière en un seul appel : le faire
   * test par test via {@link getMyAttempts} coûtait une requête par test
   * affiché.</p>
   */
  getMySessionsBySubject(
    subjectId: number,
  ): Observable<ApiCollectionResponse<any>> {
    return this.http.get<ApiCollectionResponse<any>>(
      `${this.baseUrl}/sessions/subject/${subjectId}`,
    );
  }

  /**
   * Enregistre des réponses en cours de test, sans remettre la copie.
   *
   * <p>Le serveur remplace, question par question, ce qu'il avait déjà : on
   * peut donc envoyer chaque réponse au moment où elle est donnée. C'est ce
   * qui rend une copie reprenable ailleurs que dans le navigateur d'origine.</p>
   *
   * <p>Toujours silencieuse : c'est une sauvegarde de fond. Son échec ne doit
   * pas interrompre l'élève — la copie complète repart de toute façon à la
   * remise.</p>
   */
  saveAnswers(
    sessionId: number,
    answers: { questionId: number; answerId: number | null; textAnswer?: string; numericAnswer?: number; timeSpentSeconds?: number }[],
    timeSpentSeconds?: number,
  ): Observable<ApiSingleResponse<any>> {
    return this.http.post<ApiSingleResponse<any>>(
      `${this.baseUrl}/sessions/${sessionId}/answers`,
      { answers, timeSpentSeconds },
      { context: new HttpContext().set(SILENT_ERRORS, true) },
    );
  }
}
